import { useCallback, useEffect, useRef, useState } from "react";
import { screenPerson, screeningMessage, validDob } from "../utils/personScreening";

export default function usePersonScreening(name, dob = "", { requireDob = false, enabled = true, context = "" } = {}) {
  const normalizedName = String(name || "").trim().replace(/\s+/g, " ");
  const date = String(dob || "").slice(0, 10);
  const key = JSON.stringify([normalizedName, date, enabled, requireDob]);
  const config = useRef(null);
  config.current = { name: normalizedName, dob: date, key, enabled, context, eligible: enabled && normalizedName.length >= 2 && (!requireDob || validDob(date)) };
  const active = useRef(null);
  const mounted = useRef(true);
  const confirming = useRef(false);
  const [state, setState] = useState({ key: "", pending: false, result: null });
  const run = useCallback(() => {
    const c = config.current;
    if (!c.eligible) return Promise.resolve(null);
    if (active.current?.key === c.key && (!active.current.completed || Date.now() - active.current.finishedAt < 5 * 60 * 1000)) return active.current.promise;
    active.current?.controller.abort();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 150000);
    setState({ key: c.key, pending: true, result: null });
    const entry = { key: c.key, controller, promise: null };
    entry.promise = screenPerson(c.name, c.dob, controller.signal).catch(error => {
      if (config.current.key !== c.key || !mounted.current) return null;
      return { status: "UNABLE_TO_CHECK", terrorismStatus: "UNABLE_TO_CHECK", pepStatus: "UNABLE_TO_CHECK", incomplete: true, matches: [],
        screeningError: error.name === "AbortError" ? "Screening took too long. Please retry or check manually." : error.message };
    }).then(result => {
      if (mounted.current && config.current.key === c.key) setState({ key: c.key, pending: false, result });
      return result;
    }).finally(() => {
      clearTimeout(timer);
      // Reuse this identity's recent result without a second automatic search during submission.
      if (active.current === entry) { entry.completed = true; entry.finishedAt = Date.now(); }
    });
    active.current = entry;
    return entry.promise;
  }, []);
  useEffect(() => {
    if (!config.current.eligible) { active.current?.controller.abort(); active.current = null; setState({ key, pending: false, result: null }); return; }
    const timer = setTimeout(() => { run(); }, 600);
    return () => { clearTimeout(timer); if (active.current?.key === key) { active.current.controller.abort(); active.current = null; } };
  }, [key, run]);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; active.current?.controller.abort(); }; }, []);
  const confirmBeforeProceed = useCallback(async () => {
    if (confirming.current) return false;
    confirming.current = true;
    const c = { ...config.current };
    try {
      if (!c.eligible) return true;
      const result = await run();
      if (!mounted.current || config.current.key !== c.key || !result) return false;
      if (config.current.context !== c.context) {
        window.alert("The form details changed while screening was running. Please review them and submit again.");
        return false;
      }
      const warning = screeningMessage(result);
      return !warning || window.confirm(`⚠️ ${warning}\n\nDo you still want to proceed?`);
    } finally { confirming.current = false; }
  }, [run]);
  return { pending: state.key === key && state.pending, result: state.key === key ? state.result : null, confirmBeforeProceed };
}
